import { env } from '@/lib/env';
import type { AuthProvider } from '@/auth/authProvider';
import {
  AccessNotProvisionedError,
  ApiError,
  NotPermittedError,
  SecondFactorEnrollmentRequiredError,
  SessionLostError,
  TotpReenrollmentRequiredError,
} from './errors';
import {
  isAccessNotProvisioned,
  isMfaEnrollmentRequired,
  isMfaTotpReenrollmentRequired,
  TOKEN_EXPIRED_HEADER,
  type ProblemDetails,
} from '@/types/problemDetails';

export interface ApiClient {
  request<T>(path: string, init?: RequestInit): Promise<T>;
  /** Like `request`, but returns the raw, already-authenticated `Response` instead of parsing it as JSON — for endpoints such as a document download that don't return JSON. */
  requestRaw(path: string, init?: RequestInit): Promise<Response>;
}

/**
 * Query and body parameters that would carry data scope to the server. The
 * server derives tenant and access scope from the token; a client that sends
 * one of these has taken a wrong turn, and the fix belongs in the API, not
 * here. Checked at runtime in dev and asserted in a unit test, because the
 * first person to add one will not read this comment.
 */
const FORBIDDEN_SCOPE_PARAMS = [
  'customerid',
  'customernumber',
  'customerno',
  'tenantid',
  'tenant',
  'locationcode',
  'location',
  'terminalcode',
  'terminal',
  'handlerlocation',
  'organisationid',
  'organizationid',
];

export function assertNoScopeParameters(path: string): void {
  const query = path.includes('?') ? path.slice(path.indexOf('?') + 1) : '';
  if (!query) return;

  for (const key of new URLSearchParams(query).keys()) {
    if (FORBIDDEN_SCOPE_PARAMS.includes(key.toLowerCase())) {
      throw new Error(
        `The client must never send a customer, tenant or location identifier ` +
          `("${key}" in "${path}"). Scope is resolved server-side from the token. ` +
          `If a screen appears to need one, the endpoint is wrong.`,
      );
    }
  }
}

async function readProblem(response: Response): Promise<ProblemDetails | null> {
  try {
    const body: unknown = await response.clone().json();
    return typeof body === 'object' && body !== null ? (body as ProblemDetails) : null;
  } catch {
    return null;
  }
}

export function createApiClient(auth: AuthProvider, baseUrl = env.VITE_API_BASE_URL): ApiClient {
  async function send(path: string, init: RequestInit, token: string): Promise<Response> {
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    headers.set('Accept', 'application/json');
    // Never on a FormData body: the browser writes multipart/form-data with
    // its boundary itself, and a Content-Type set here would drop it.
    if (
      init.body !== undefined &&
      !(init.body instanceof FormData) &&
      !headers.has('Content-Type')
    ) {
      headers.set('Content-Type', 'application/json');
    }

    return fetch(`${baseUrl}${path}`, { ...init, headers });
  }

  async function authorizedFetch(path: string, init: RequestInit): Promise<Response> {
    assertNoScopeParameters(path);

    let response = await send(path, init, await auth.getToken());

    if (response.status === 401) {
      const problem = await readProblem(response);

      // Order matters. The unprovisioned discriminator is checked before
      // anything token-shaped, because "401 means sign in again" is the default
      // instinct and it produces an infinite redirect for exactly the users
      // invite-only onboarding creates.
      if (isAccessNotProvisioned(problem)) {
        throw new AccessNotProvisionedError(problem);
      }

      // The MFA refusals come next, and for the same reason: the sign-in is
      // fine and a fresh token would say the same thing, so treating them as
      // "session lost" sends every enrolled-but-unsatisfied user round the
      // sign-in screen forever. After the unprovisioned check, because the
      // backend never sends an MFA code to an unprovisioned user; before the
      // Token-Expired branch, because neither of these is expiry.
      if (isMfaEnrollmentRequired(problem)) {
        throw new SecondFactorEnrollmentRequiredError(problem);
      }
      if (isMfaTotpReenrollmentRequired(problem)) {
        throw new TotpReenrollmentRequiredError(problem);
      }

      if (response.headers.get(TOKEN_EXPIRED_HEADER)?.toLowerCase() === 'true') {
        // Exactly one retry. A retry loop against an expired session is an
        // infinite redirect, so there is no loop here and there must never be.
        let freshToken: string;
        try {
          freshToken = await auth.getToken({ forceRefresh: true });
        } catch {
          // getIdToken(true) itself throws when the backend has revoked the
          // refresh token (the B5 deactivation path): auth/user-token-expired,
          // auth/user-disabled. Not a retryable state — sign out rather than
          // looping on a session that can never come back.
          await auth.signOut();
          throw new SessionLostError(problem);
        }

        response = await send(path, init, freshToken);

        if (response.status === 401) {
          throw new SessionLostError(await readProblem(response));
        }
      } else {
        throw new SessionLostError(problem);
      }
    }

    if (response.status === 403) {
      throw new NotPermittedError(await readProblem(response));
    }

    if (!response.ok) {
      const problem = await readProblem(response);
      throw new ApiError(
        response.status,
        problem,
        problem?.title ?? `Request failed with status ${response.status}.`,
      );
    }

    return response;
  }

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await authorizedFetch(path, init);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  async function requestRaw(path: string, init: RequestInit = {}): Promise<Response> {
    return authorizedFetch(path, init);
  }

  return { request, requestRaw };
}
